import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { accounts as accountsApi, riskGates } from "../api/endpoints";
import type { Account, PatchAccountRequest } from "../api/types";
import { ApiError } from "../api/client";

// Post-creation editor for the per-account risk knobs. Backend
// already accepts these via PATCH /api/accounts/{id}; SetupPage
// exposes them at creation time only, so there was no way to change
// them on a live account without poking the DB by hand.
//
// "0" in any numeric field clears the override and falls the account
// back to the env / process-wide default (NEXUS_NLV_FLOOR_USD,
// NEXUS_DAILY_LOSS_PCT_BALANCE). max_contracts can't be cleared —
// the server rejects max_contracts=0 — so we min-clamp at 1.
interface Props {
  account: Account;
}

export function RiskCard({ account }: Props) {
  const qc = useQueryClient();

  const [maxC, setMaxC] = useState(String(account.max_contracts ?? ""));
  const [dailyPct, setDailyPct] = useState(
    account.daily_loss_pct_balance !== null && account.daily_loss_pct_balance !== undefined
      ? String(account.daily_loss_pct_balance)
      : "",
  );
  const [nlv, setNlv] = useState(
    account.nlv_floor_usd !== null && account.nlv_floor_usd !== undefined
      ? String(account.nlv_floor_usd)
      : "",
  );
  // Portfolio label is operator-derived from the deploy template, not an
  // end-user setting -- removed from the editable UI. Backend still
  // accepts portfolio_label via PATCH; SetupPage seeds it at account-
  // creation time.
  const [err, setErr] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  // Sync local state when the upstream Account row changes (e.g.,
  // another tab patched it, or the refetch picked up our own write).
  useEffect(() => {
    setMaxC(String(account.max_contracts ?? ""));
    setDailyPct(
      account.daily_loss_pct_balance !== null && account.daily_loss_pct_balance !== undefined
        ? String(account.daily_loss_pct_balance)
        : "",
    );
    setNlv(
      account.nlv_floor_usd !== null && account.nlv_floor_usd !== undefined
        ? String(account.nlv_floor_usd)
        : "",
    );
  }, [account]);

  const save = useMutation({
    mutationFn: async () => {
      const body: PatchAccountRequest = {};
      const maxN = Number(maxC);
      if (!Number.isFinite(maxN) || maxN < 1) {
        throw new Error("max contracts must be 1 or higher");
      }
      if (maxN !== account.max_contracts) body.max_contracts = maxN;

      // Empty input = "leave alone." 0 = "clear the override" (server
      // converts to NULL, kill_watchdog falls back to env).
      const trimDaily = dailyPct.trim();
      if (trimDaily !== "") {
        const dN = Number(trimDaily);
        if (!Number.isFinite(dN) || dN < 0 || dN > 100) {
          throw new Error("daily loss % must be between 0 and 100");
        }
        if (dN !== (account.daily_loss_pct_balance ?? -1)) {
          body.daily_loss_pct_balance = dN;
        }
      }
      const trimNlv = nlv.trim();
      if (trimNlv !== "") {
        const nN = Number(trimNlv);
        if (!Number.isFinite(nN) || nN < 0) {
          throw new Error("NLV floor must be 0 or higher");
        }
        if (nN !== (account.nlv_floor_usd ?? -1)) {
          body.nlv_floor_usd = nN;
        }
      }
      if (Object.keys(body).length === 0) {
        throw new Error("nothing changed");
      }
      return accountsApi.patch(account.id, body);
    },
    onSuccess: () => {
      setErr(null);
      setOkMsg("saved");
      setTimeout(() => setOkMsg(null), 1500);
      qc.invalidateQueries({ queryKey: ["accounts"] });
      qc.invalidateQueries({ queryKey: ["account", account.id] });
    },
    onError: (e) => {
      setOkMsg(null);
      setErr(e instanceof ApiError ? e.message : (e instanceof Error ? e.message : String(e)));
    },
  });

  return (
    <div className="card">
      <h2>Risk config</h2>
      <div className="muted small" style={{ marginBottom: 8 }}>
        Edits apply immediately — kill_watchdog re-reads on every tick.
        Set a numeric field to <span className="mono">0</span> to clear the
        override and fall back to the process default.
      </div>
      {err && <div className="error-banner" style={{ marginBottom: 8 }}>{err}</div>}
      {okMsg && (
        <div className="muted small" style={{ marginBottom: 8, color: "var(--good, #5a5)" }}>
          ✓ {okMsg}
        </div>
      )}
      <div className="grid grid-2">
        <label>
          <span className="label-text">Max contracts</span>
          <input
            value={maxC}
            onChange={(e) => setMaxC(e.target.value)}
            inputMode="numeric"
          />
          <span className="label-help">
            hard ceiling on signed qty per dispatch (across all sleeves)
          </span>
        </label>
        <label>
          <span className="label-text">Daily loss cap (% of balance)</span>
          <input
            value={dailyPct}
            onChange={(e) => setDailyPct(e.target.value)}
            inputMode="decimal"
            placeholder="blank = default"
          />
          <span className="label-help">
            At {dailyPct ? Number(dailyPct) : "X"}% × ${account.rithmic?.last_balance ?? account.starting_balance ?? 0} = ~${Math.round((Number(dailyPct || 0) / 100) * (account.rithmic?.last_balance ?? account.starting_balance ?? 0))} daily max loss. Set 0 to clear.
          </span>
        </label>
        <label>
          <span className="label-text">NLV floor ($)</span>
          <input
            value={nlv}
            onChange={(e) => setNlv(e.target.value)}
            inputMode="decimal"
            placeholder="blank = default"
          />
          <span className="label-help">
            If your account balance drops below this, kill_watchdog flattens everything. NLV = Net Liquid Value (cash + unrealized PnL). Set 0 to clear → env default.
          </span>
        </label>
      </div>
      <hr />
      <BindingGates account={account} />
      <hr />
      <div className="row">
        <div className="spacer" />
        <button
          className="primary"
          onClick={() => save.mutate()}
          disabled={save.isPending}
        >
          {save.isPending ? "saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

/// Read-only view of the BINDING dispatch-time risk gates
/// (`risk_gates` row for this account's broker). These are enforced
/// per-order by the risk-gate chain BEFORE any order leaves the engine —
/// distinct from the editable `accounts.*` knobs above, which feed
/// kill_watchdog. An incident-time cap check must read this panel.
function BindingGates({ account }: Props) {
  const q = useQuery({
    queryKey: ["risk_gates", account.broker, account.id],
    queryFn: () => riskGates.get(account.broker, account.id),
    staleTime: 30_000,
    retry: 1,
  });

  if (q.isLoading) return <div className="muted small">loading binding gates…</div>;
  if (q.isError) {
    return (
      <div className="muted small">
        Binding risk gates: unavailable ({q.error instanceof Error ? q.error.message : "error"})
      </div>
    );
  }
  const g = q.data;
  const fmt = (v: number | null | undefined, suffix = ""): string =>
    v === null || v === undefined ? "—" : `${v}${suffix}`;

  return (
    <div>
      <h3 style={{ marginBottom: 4 }}>Binding dispatch gates (risk_gates)</h3>
      <div className="muted small" style={{ marginBottom: 8 }}>
        Enforced per-order by the risk-gate chain BEFORE dispatch. Edit via
        PATCH <span className="mono">/api/risk_gates/{account.broker}/{account.id}</span> or the
        DB. NULL = no override (env default applies).
      </div>
      <div className="grid grid-2">
        <div>
          <span className="label-text">Max contracts per dispatch</span>
          <div className="mono">{fmt(g?.max_contracts)}</div>
        </div>
        <div>
          <span className="label-text">Max drawdown ($)</span>
          <div className="mono">{fmt(g?.max_drawdown_usd, "$")}</div>
        </div>
        <div>
          <span className="label-text">Daily loss cap ($)</span>
          <div className="mono">{fmt(g?.daily_loss_cap_usd, "$")}</div>
        </div>
        <div>
          <span className="label-text">Max orders / minute</span>
          <div className="mono">{fmt(g?.max_orders_per_minute)}</div>
        </div>
        <div>
          <span className="label-text">Max concurrent direction</span>
          <div className="mono">{fmt(g?.max_concurrent_direction)}</div>
        </div>
      </div>
    </div>
  );
}
